import { Component, ErrorInfo, ReactNode } from "react";

interface IErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
  /**
   * Clears the caught error whenever it changes — typically the current
   * pathname, so navigating away recovers.
   *
   * Deliberately a prop rather than a `key` on the boundary: a changed `key`
   * makes React treat this as a new element and unmount the whole subtree,
   * which would remount `DashboardLayout` (re-running `getAvailableWards` and
   * the ward-preference cascade) and `PatientPage` on every navigation.
   */
  resetKey?: unknown;
}

interface IErrorBoundaryState {
  hasError: boolean;
}

/**
 * EN-2750. The portal had no error boundary at all, so any throw during render
 * unmounted the whole tree and left the user staring at a blank page — the
 * usual trigger being a failed API call whose error body reaches a query hook
 * as if it had succeeded (see the interceptor in services/api.ts).
 *
 * Hand-rolled because react-router's `errorElement` isn't available here: the
 * app builds its routes with `useRoutes`, not `createBrowserRouter`.
 */
export class ErrorBoundary extends Component<
  IErrorBoundaryProps,
  IErrorBoundaryState
> {
  constructor(props: IErrorBoundaryProps) {
    super(props);

    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): IErrorBoundaryState {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: IErrorBoundaryProps) {
    const { hasError } = this.state;
    const { resetKey } = this.props;

    // Guarded on `hasError` so an ordinary navigation doesn't setState on
    // every boundary that never caught anything.
    if (hasError && prevProps.resetKey !== resetKey) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Unhandled render error", error, errorInfo.componentStack);
  }

  render() {
    const { hasError } = this.state;
    const { children, fallback } = this.props;

    return hasError ? fallback : children;
  }
}
